/**
 * The backtest handoff package (JF 2026-10-16): the backtest library for another project (PP Checker), which
 * calibrates on what the customer was quoted, not on our cost. One zip:
 *   清单.xlsx / 清单.json — one row per style: facts, the customer quote (sell, GP %), our real cost (FOB / materials / CM)
 *   说明.md — what the fields mean and the rules that keep two backtests comparable
 *   文件/<style>/<file> — the one tech-pack file of each style
 */
import ExcelJS from "exceljs";
import JSZip from "jszip";
import type { BtTruth } from "../../shared/api.ts";

export interface PackageCase { name: string; fileName: string; sc: string; truth: BtTruth | null; file: string | null; library?: string; totalOnly?: boolean }

const safe = (s: string) => s.replace(/[\\/:*?"<>|]+/g, "_").trim() || "_";
const r2 = (x: number | null | undefined) => (x == null ? null : Math.round(x * 100) / 100);

/** one row of the list, in the field names the json uses */
export function packageRow(c: PackageCase) {
  const t = c.truth;
  return {
    library: c.library || "利华历史款", totalOnly: !!c.totalOnly, style: c.name, sc: c.sc, styleNo: t?.styleNo ?? "", customer: t?.customer ?? "", category: t?.sub ?? "", qty: t?.qty ?? null,
    exFactory: (t?.exFty || "").slice(0, 10), tradeTerm: t?.tradeTerm ?? "", factory: t?.factory ?? "", country: t?.country ?? "",
    file: c.file ? `文件/${safe(c.name)}/${safe(c.fileName)}` : null,
    customerQuoteUsd: r2(t?.sell), customerGpPct: t?.gp == null ? null : Math.round(t.gp * 1000) / 10,
    ourCostFobUsd: r2(t?.fob), ourMaterialsUsd: c.totalOnly ? null : r2(t?.mat), ourCmUsd: c.totalOnly ? null : r2(t?.cm),
  };
}

export const README = (n: number, at: string) => `# 回测库交接包 / Backtest library handoff

导出时间 / Exported: ${at.slice(0, 16).replace("T", " ")} UTC · ${n} 款 / styles
来源 / Source: Instant Costing（AutoCosting）后台「回测」页 · 真实数字取自公司数仓历史（成本表 + GP 表）

## 这是什么 / What this is
${n} 个公司真实做过、已经走货的款。每款只有一个 tech pack 文件（\`文件/\` 下），以及两组"答案"：
- **给客户的报价**（customerQuoteUsd，每件，美元）和 GP%（customerGpPct）——PP Checker 用这个校准。
- **我们的真实成本**（ourCostFobUsd 成本 FOB、ourMaterialsUsd 面辅料、ourCmUsd 加工费 CM，每件，美元）——Instant Costing 用这个校准。

## 字段 / Fields（清单.xlsx = 清单.json）
| 字段 | 含义 |
|---|---|
| style | 款（文件夹名） |
| sc | 合同号（数仓里的 SC） |
| styleNo / customer / category | 款号 / 客户 / 品类（子类） |
| qty | 这单的件数 |
| exFactory | 出厂日期 |
| tradeTerm | 贸易条款（FOB / CMT…）——**不是 FOB 的款不要直接和 FOB 比** |
| file | 这款用的那一个 tech pack 文件 |
| customerQuoteUsd / customerGpPct | 给客户的报价（每件）/ GP% |
| ourCostFobUsd / ourMaterialsUsd / ourCmUsd | 我们的成本 FOB / 面辅料 / CM（每件） |

## 规则（两边的回测才可比）/ Rules
1. **每款只用这一个文件。** 不要再喂别的文件，尤其是带价格的订单 / 发票 / 报价单——那等于把答案给了 AI。
2. **件数当作已知**：业务本来就知道件数，可以作为补充说明告诉 AI（Instant Costing 是这样做的："Order quantity: N pcs"）。
3. 偏差 = (估出来的 − 真实) ÷ 真实。看整体时用平均绝对偏差、中位数偏差、整体偏高 / 偏低，以及 ±10% / ±20% 以内几款。
4. 贸易条款不是 FOB 的款（例如 CMT）单独看，或者不计入。
5. 这是快照；回测库加了新款，在 Instant Costing 后台「回测」→「导出交接包」重新导出即可。
`;

export async function buildPackage(cases: PackageCase[], exportedAt: string): Promise<Blob> {
  const zip = new JSZip();
  const rows = cases.map(packageRow);
  zip.file("清单.json", JSON.stringify({ exportedAt, source: "Instant Costing backtest library", styles: rows }, null, 2));
  zip.file("说明.md", README(cases.length, exportedAt));
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("回测库 Backtest");
  const cols: [keyof ReturnType<typeof packageRow>, string, number][] = [
    ["library", "分组 Library", 12], ["style", "款 Style", 18], ["sc", "SC", 14], ["styleNo", "款号 Style no.", 18], ["customer", "客户 Customer", 18], ["category", "品类 Category", 16],
    ["qty", "件数 Qty", 10], ["exFactory", "出厂 Ex-factory", 13], ["tradeTerm", "贸易条款 Trade term", 12],
    ["customerQuoteUsd", "给客户的报价 $/件 Customer quote", 18], ["customerGpPct", "GP %", 8],
    ["ourCostFobUsd", "我们的成本 FOB $/件 Our cost FOB", 18], ["ourMaterialsUsd", "面辅料 $/件 Materials", 14], ["ourCmUsd", "CM $/件", 10],
    ["file", "文件 File", 60],
  ];
  ws.columns = cols.map(([key, header, width]) => ({ key, header, width }));
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).alignment = { wrapText: true, vertical: "middle" };
  ws.getRow(1).height = 32;
  for (const r of rows) ws.addRow(r);
  for (const k of ["customerQuoteUsd", "ourCostFobUsd", "ourMaterialsUsd", "ourCmUsd"]) ws.getColumn(k).numFmt = "$#,##0.00";
  ws.views = [{ state: "frozen", ySplit: 1, xSplit: 1 }];
  zip.file("清单.xlsx", await wb.xlsx.writeBuffer());
  for (const c of cases) if (c.file) zip.file(`文件/${safe(c.name)}/${safe(c.fileName)}`, c.file, { base64: true });
  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
